import { useState } from "react";
import { Loader2, Search, Trash2 } from "lucide-react";
import type { FilterDto, MessageSummaryDto } from "../../../shared/types";
import {
  useAiGroupEmails,
  useAiUsage,
  useDeletePin,
  useFilters,
  useModels,
  usePinEmail,
  useSaveSettings,
  useSearchMessages,
  useSettings,
  useUpdatePin,
} from "../../hooks/useApi";
import { SWATCH_CLASSES, badgeClass } from "../filterColors";
import { AiGroupDialog } from "./AiGroupDialog";

const CALENDAR_VIEWS = [
  "day",
  "week",
  "workWeek",
  "month",
  "timelineDay",
  "timelineWeek",
  "timelineMonth",
  "agenda",
] as const;

const inputClass =
  "w-full rounded border border-stroke px-2.5 py-1.5 text-[13px] outline-none focus:border-accent";
const labelClass = "pb-1 pt-2 text-[12px] font-medium text-ink-muted";

function MailTab() {
  const { data: filters, isLoading } = useFilters();
  // Which group the studio dialog is editing; "new" = creating.
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [subTab, setSubTab] = useState<"groups" | "pins">("groups");

  const list = [...(filters ?? [])].sort((a, b) => a.order - b.order);
  const editingFilter = typeof editing === "number" ? list.find((f) => f.id === editing) : undefined;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1">
        {(
          [
            ["groups", "Groups"],
            ["pins", "Pinned emails"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSubTab(id)}
            className={`rounded px-2.5 py-1 text-[12px] ${
              subTab === id ? "bg-accent-subtle font-semibold text-accent" : "text-ink-muted hover:text-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {subTab === "groups" ? (
        <>
          <p className="text-[12px] text-ink-muted">
            AI groups categorize your mail. Click a group to edit its prompt, or ＋ to create one —
            describe what belongs in the group and let the AI write the prompt, then test it before
            saving.
          </p>

          {isLoading && <p className="text-[13px] text-ink-muted">Loading…</p>}

          {/* Badge per group (click = edit) plus a "+" to create a new one. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {list.map((f: FilterDto) => (
              <button
                key={f.id}
                onClick={() => setEditing(f.id)}
                title={`Edit ${f.name}`}
                className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium ${badgeClass(f.color)} opacity-80 hover:opacity-100 hover:ring-2 hover:ring-accent hover:ring-offset-1`}
              >
                {f.name}
              </button>
            ))}
            <button
              onClick={() => setEditing("new")}
              title="Add AI group"
              className="grid size-6 place-items-center rounded-full border border-dashed border-stroke text-[14px] text-ink-muted hover:border-accent hover:text-accent"
            >
              +
            </button>
          </div>

          {!isLoading && list.length === 0 && (
            <p className="text-[13px] text-ink-muted">No AI groups yet — add one with ＋.</p>
          )}

          {editing !== null && (
            <AiGroupDialog
              initial={editingFilter}
              defaultOrder={list.length + 1}
              onClose={() => setEditing(null)}
            />
          )}
        </>
      ) : (
        <PinnedEmailsPanel groups={list} />
      )}
    </div>
  );
}

/** Settings list of manual pins: view, move to another group, delete, add. */
function PinnedEmailsPanel({ groups }: { groups: FilterDto[] }) {
  const { data: pins, isLoading } = useAiGroupEmails();
  const updatePin = useUpdatePin();
  const deletePin = useDeletePin();
  const pinEmail = usePinEmail();
  const searchMessages = useSearchMessages(0);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MessageSummaryDto[] | null>(null);

  const groupOf = (id: number) => groups.find((g) => g.id === id);

  const runSearch = async () => {
    if (query.trim().length < 3) return;
    const res = await searchMessages.mutateAsync({ subject: query.trim() });
    setResults(res.messages.slice(0, 8));
  };

  const pinFromSearch = (m: MessageSummaryDto, filterId: number) => {
    const accountId = m.origin?.accountId;
    if (!accountId) return;
    const rawId = m.id.startsWith(`${accountId}:`) ? m.id.slice(`${accountId}:`.length) : m.id;
    pinEmail.mutate(
      { filterId, accountId, messageId: rawId },
      { onSuccess: () => setResults(null) },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[12px] text-ink-muted">
        Pinned emails always belong to their chosen group — the AI never re-judges them. Deleting a
        pin puts the email back under AI categorization.
      </p>

      {isLoading && <p className="text-[13px] text-ink-muted">Loading…</p>}
      {!isLoading && !(pins ?? []).length && (
        <p className="text-[13px] text-ink-muted">
          No pinned emails yet — hover an email in the list and click the ✨ icon.
        </p>
      )}

      {(pins ?? []).map((pin) => (
        <div key={pin.id} className="flex items-center gap-2 rounded border border-stroke p-2">
          <span
            className={`size-2.5 shrink-0 rounded-full ${SWATCH_CLASSES[groupOf(pin.filterId)?.color ?? "gray"]}`}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px]">{pin.subject || "(no subject)"}</p>
            <p className="truncate text-[11px] text-ink-muted">
              {pin.fromName ?? pin.fromAddress ?? "unknown"} ·{" "}
              {new Date(pin.createdAt).toLocaleDateString()}
            </p>
          </div>
          <select
            value={pin.filterId}
            onChange={(e) => updatePin.mutate({ id: pin.id, filterId: Number(e.target.value) })}
            className="shrink-0 rounded border border-stroke bg-white px-1.5 py-0.5 text-[12px]"
            title="Move to another group"
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => deletePin.mutate(pin.id)}
            title="Remove pin (back to AI categorization)"
            aria-label="Remove pin"
            className="grid size-6 shrink-0 place-items-center rounded text-ink-muted hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}

      {/* Add an email by searching the local store. */}
      <div className="mt-2 border-t border-stroke pt-2">
        <p className="pb-1 text-[12px] font-medium text-ink-muted">Add email (search by subject)</p>
        <div className="flex gap-1.5">
          <input
            className={inputClass}
            placeholder="Type at least 3 characters…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
          />
          <button
            onClick={runSearch}
            disabled={query.trim().length < 3 || searchMessages.isPending}
            className="grid size-8 shrink-0 place-items-center rounded border border-stroke text-ink-muted hover:text-accent disabled:opacity-40"
            aria-label="Search emails"
          >
            {searchMessages.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Search size={14} />
            )}
          </button>
        </div>
        {results && (
          <div className="flex flex-col gap-1 pt-2">
            {!results.length && <p className="text-[12px] text-ink-muted">No matches.</p>}
            {results.map((m) => (
              <div key={m.id} className="flex items-center gap-2 rounded border border-stroke p-1.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px]">{m.subject}</p>
                  <p className="truncate text-[11px] text-ink-muted">{m.from?.name}</p>
                </div>
                <select
                  defaultValue=""
                  onChange={(e) => e.target.value && pinFromSearch(m, Number(e.target.value))}
                  className="shrink-0 rounded border border-stroke bg-white px-1.5 py-0.5 text-[12px]"
                >
                  <option value="" disabled>
                    Pin to…
                  </option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CalendarTab() {
  const { data: settings } = useSettings();
  const saveSettings = useSaveSettings();
  if (!settings) return <p className="text-[13px] text-ink-muted">Loading…</p>;

  return (
    <div>
      <p className="pb-1 text-[12px] text-ink-muted">Applies to the account Dashboard calendar.</p>
      <div className="flex gap-3">
        <div className="flex-1">
          <p className={labelClass}>Day start hour</p>
          <input
            type="number"
            min={0}
            max={23}
            className={inputClass}
            value={settings.calendarStartHour}
            onChange={(e) => {
              const h = Math.max(0, Math.min(23, Number(e.target.value)));
              if (h < settings.calendarEndHour) saveSettings.mutate({ calendarStartHour: h });
            }}
          />
        </div>
        <div className="flex-1">
          <p className={labelClass}>Day end hour</p>
          <input
            type="number"
            min={1}
            max={24}
            className={inputClass}
            value={settings.calendarEndHour}
            onChange={(e) => {
              const h = Math.max(1, Math.min(24, Number(e.target.value)));
              if (h > settings.calendarStartHour) saveSettings.mutate({ calendarEndHour: h });
            }}
          />
        </div>
      </div>
      <p className={labelClass}>Default view</p>
      <select
        className={inputClass}
        value={settings.calendarDefaultView}
        onChange={(e) => saveSettings.mutate({ calendarDefaultView: e.target.value })}
      >
        {CALENDAR_VIEWS.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    </div>
  );
}

function formatPrice(p: number | null): string {
  return p === null ? "price n/a" : `$${p.toFixed(2)}/1M`;
}

function formatUsd(v: number): string {
  return v < 0.01 && v > 0 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

function AITab() {
  const { data: settings } = useSettings();
  const { data: models, isLoading } = useModels();
  const { data: usage } = useAiUsage();
  const saveSettings = useSaveSettings();
  if (!settings) return <p className="text-[13px] text-ink-muted">Loading…</p>;

  const current = models?.find((m) => m.id === settings.aiModel);

  return (
    <div>
      <p className="pb-1 text-[12px] text-ink-muted">
        Model used for mail filtering (✨) and AI search.
      </p>
      <p className={labelClass}>Model</p>
      <select
        className={inputClass}
        value={settings.aiModel}
        onChange={(e) => saveSettings.mutate({ aiModel: e.target.value })}
      >
        {isLoading && <option>Loading models…</option>}
        {(models ?? []).map((m) => (
          <option key={m.id} value={m.id}>
            {m.name} — {formatPrice(m.promptPricePerM)} in · {formatPrice(m.completionPricePerM)} out
          </option>
        ))}
      </select>
      {current && (
        <p className="pt-2 text-[12px] text-ink-muted">
          Selected: <span className="font-medium text-ink">{current.name}</span> —{" "}
          {formatPrice(current.promptPricePerM)} input · {formatPrice(current.completionPricePerM)}{" "}
          output tokens.
        </p>
      )}

      {usage && (
        <div className="mt-4 rounded border border-stroke bg-surface p-3">
          <p className="pb-1 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
            OpenRouter usage
          </p>
          <div className="flex gap-8">
            <div>
              <p className="text-[18px] font-semibold leading-6">{formatUsd(usage.totalUsd)}</p>
              <p className="text-[11px] text-ink-muted">total</p>
            </div>
            <div>
              <p className="text-[18px] font-semibold leading-6">{formatUsd(usage.last30DaysUsd)}</p>
              <p className="text-[11px] text-ink-muted">last 30 days</p>
            </div>
            <div>
              <p className="text-[18px] font-semibold leading-6">{usage.calls.toLocaleString()}</p>
              <p className="text-[11px] text-ink-muted">API calls</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type SettingsTab = "mail" | "calendar" | "ai";

export function SettingsDialog({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<SettingsTab>("mail");

  const tabs: { id: SettingsTab; label: string }[] = [
    { id: "mail", label: "AI Group" },
    { id: "calendar", label: "Calendar" },
    { id: "ai", label: "AI" },
  ];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="flex max-h-[85vh] w-[560px] max-w-[92vw] flex-col rounded-lg border border-stroke bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-stroke px-4 py-3">
          <h2 className="text-[15px] font-semibold">Settings</h2>
          <button
            onClick={onClose}
            aria-label="Close settings"
            className="rounded px-2 text-[16px] text-ink-muted hover:bg-black/5"
          >
            ×
          </button>
        </div>

        <div className="flex items-stretch gap-1 border-b border-stroke px-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative px-3 py-2 text-[13px] ${
                tab === t.id ? "font-semibold text-accent" : "text-ink-muted hover:text-ink"
              }`}
            >
              {t.label}
              {tab === t.id && (
                <span className="absolute inset-x-2 bottom-0 h-[2px] rounded-t bg-accent" />
              )}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto p-4">
          {tab === "mail" && <MailTab />}
          {tab === "calendar" && <CalendarTab />}
          {tab === "ai" && <AITab />}
        </div>
      </div>
    </div>
  );
}
